/**
 * Import a price chart (Excel) into the SHARED price book + dropdowns.
 * -----------------------------------------------------------------------------
 * Used by BOTH the Sales Order and Diamond Purchase pages. Reads a sheet with
 * columns like:  Shape | Party Quality | Slive | Size | Code | purchase | Sale
 * and:
 *   - fills the price book keyed by SHAPE | QUALITY | SLIVE → { p: purchase, s: sale }
 *     (so Sale Rate and Purchase Rate auto-fill everywhere),
 *   - merges Shape / Quality / Slive / Size / Code into the dropdown pick-lists
 *     (UPPERCASED, deduped — never removes what the team already added).
 *
 * Everything is saved to the backend database via the stores' shared storage.
 * Merges with existing data (does not wipe), so you can import more than once.
 */
import * as XLSX from 'xlsx'
import { usePriceBookStore, priceKey, type PriceEntry } from './priceBookStore'
import { useSalesBookStore } from './salesBookStore'

const up = (v: unknown) => String(v ?? '').trim().replace(/\s+/g, ' ').toUpperCase()
const raw = (v: unknown) => String(v ?? '').trim()
const num = (v: unknown) => { const n = parseFloat(String(v).replace(/[^0-9.-]/g, '')); return Number.isFinite(n) ? n : 0 }

export interface ImportResult { rows: number; combos: number }

export async function importPriceChart(file: File): Promise<ImportResult> {
  const buf = await file.arrayBuffer()
  const wb = XLSX.read(buf, { type: 'array' })
  const ws = wb.Sheets[wb.SheetNames[0]]
  const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, blankrows: false })

  // Densify each row ([...] fills sparse holes with undefined) so an empty first
  // column (like the blank "A" cell in the header) never breaks the lookups.
  const cells = (row: unknown[] | undefined) => [...(row || [])].map((c) => String(c ?? '').trim().toLowerCase())

  // Find the header row (has "shape" and "purchase"/"sale").
  let hi = -1
  for (let i = 0; i < Math.min(grid.length, 20); i++) {
    const low = cells(grid[i])
    if (low.some((c) => c.includes('shape')) && low.some((c) => c.includes('purchase') || c.includes('sale'))) { hi = i; break }
  }
  if (hi < 0) throw new Error('Header row not found. Needs columns: Shape, Quality, Slive, purchase, Sale.')

  const header = cells(grid[hi])
  const col = (...names: string[]) => header.findIndex((h) => names.some((n) => h.includes(n)))
  const iShape = col('shape'), iQual = col('quality'), iSlive = col('slive', 'sieve'),
    iSize = col('size'), iCode = col('code'), iPur = col('purchase'), iSale = col('sale')
  if (iShape < 0 || iQual < 0 || iSlive < 0) throw new Error('Missing Shape / Quality / Slive column in the sheet.')

  const book: Record<string, PriceEntry> = { ...usePriceBookStore.getState().book }
  const shapes = new Set<string>(), quals = new Set<string>(), slives = new Set<string>(), sizes = new Set<string>(), codes = new Set<string>()
  let rows = 0

  for (let r = hi + 1; r < grid.length; r++) {
    const row = grid[r]; if (!row) continue
    const shape = up(row[iShape]), quality = up(row[iQual]), slive = up(row[iSlive])
    if (!shape || !quality || !slive) continue
    const purchase = iPur >= 0 ? num(row[iPur]) : 0
    const sale = iSale >= 0 ? num(row[iSale]) : 0
    const key = priceKey(shape, quality, slive)
    const prev = book[key] || { p: 0, s: 0 }
    // keep any existing price if this row leaves one blank
    book[key] = { p: purchase || prev.p, s: sale || prev.s }
    shapes.add(shape); quals.add(quality); slives.add(slive)
    if (iSize >= 0 && raw(row[iSize])) sizes.add(up(row[iSize]))
    if (iCode >= 0 && raw(row[iCode])) codes.add(up(row[iCode]))
    rows++
  }
  if (rows === 0) throw new Error('No valid rows found (need Shape, Quality and Slive on each row).')

  usePriceBookStore.getState().setBook(book)

  const st = useSalesBookStore.getState()
  const uni = (cur: string[], add: Set<string>) => Array.from(new Set([...cur, ...add]))
  // Add shapes/qualities/codes from the price chart, but DO NOT touch the Sieve/Size
  // lists — those stay the founder's exact clean list; employees add via "+".
  st.setShapes(uni(st.shapes, shapes))
  st.setQualities(uni(st.qualities, quals))
  st.setCodes(uni(st.codes, codes))
  void slives; void sizes

  return { rows, combos: Object.keys(book).length }
}
